import { useNavigate } from 'react-router-dom';
import type { Scene } from '@/types';
import { SceneCell } from './SceneCell';
import { useEpisodeStore } from '@/state/episodeStore';
import { useSelectionStore } from '@/state/selectionStore';
import { deleteScene } from '@/services/scenesApi';
import { runMutation } from '@/state/saveQueue';
import { toast } from '@/state/toastStore';

interface Props {
  scenes: Scene[];
  episodeUuid: string;
}

export function SceneGrid({ scenes, episodeUuid }: Props) {
  const navigate = useNavigate();
  const activeSceneId = useSelectionStore((s) => s.activeSceneId);
  const referenceSceneIds = useSelectionStore((s) => s.pendingReferences.sceneIds);
  const activateScene = useSelectionStore((s) => s.activateScene);
  const toggleRef = useSelectionStore((s) => s.toggleReferenceScene);
  const sceneById = useEpisodeStore((s) => s.scenes);
  const upsertScene = useEpisodeStore((s) => s.upsertScene);

  async function handleDelete(scene: Scene) {
    if (!confirm(`Delete scene ${String(scene.order).padStart(2, '0')}?`)) return;
    const previous = scene;
    upsertScene({ ...scene, deletedAt: Date.now() });
    if (scene.id === activeSceneId) activateScene(null);
    try {
      await runMutation(
        {
          episodeUuid,
          endpoint: `/v2/scenes/${scene.id}`,
          method: 'DELETE',
          body: {},
        },
        () => deleteScene(episodeUuid, scene.id, scene.version),
      );
      toast.ok('Scene deleted.');
    } catch (err) {
      upsertScene(previous);
      toast.err(err instanceof Error ? err.message : 'Delete failed.');
    }
  }

  const visible = scenes
    .filter((s) => !s.deletedAt)
    .sort((a, b) => a.order - b.order);

  if (visible.length === 0) {
    return <EmptyGrid />;
  }

  const linkedSet = new Set(referenceSceneIds);
  const hasActive = activeSceneId !== null;

  function handleCellClick(scene: Scene) {
    // If scene already has images/videos, open detail page
    if (scene.imageIds.length > 0 || scene.videoIds.length > 0) {
      navigate(`/episode/${episodeUuid}/scene/${scene.id}`);
      return;
    }
    // Empty cell — toggle: clicking the active one again deactivates.
    if (scene.id === activeSceneId) {
      activateScene(null);
      return;
    }
    activateScene(scene);
  }

  function handleShiftClick(scene: Scene) {
    // Need an active scene first; if none, activate this one with no refs
    const active = sceneById.find((s) => s.id === activeSceneId);
    if (!active) {
      activateScene(scene);
      return;
    }
    // Don't reference self
    if (scene.id === active.id) return;
    toggleRef(scene.id);
  }

  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}
    >
      {visible.map((scene) => (
        <SceneCell
          key={scene.id}
          scene={scene}
          episodeUuid={episodeUuid}
          onClick={handleCellClick}
          onShiftClick={handleShiftClick}
          onDelete={() => handleDelete(scene)}
          isActive={scene.id === activeSceneId}
          isLinked={linkedSet.has(scene.id)}
          isInLinkedSet={linkedSet.has(scene.id)}
          hasActiveScene={hasActive}
        />
      ))}
    </div>
  );
}

function EmptyGrid() {
  return (
    <div className="flex h-full min-h-[40vh] flex-col items-center justify-center text-center">
      <p className="font-mono text-[12px] text-text-4">no scenes yet</p>
      <p className="mt-2 max-w-sm text-[14px] text-text-3">
        Type a prompt below to create your first scene, or describe a story in batch mode.
      </p>
    </div>
  );
}
